import * as React from "react";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Box, Skeleton, Typography, alpha } from "@mui/material";
import ViewDetails from "../genericComponents/view-details/ViewDetails";
import {
  BasicProgram,
  ResponseObject,
  ViewDetailsServices,
} from "@/app/classes/main";
import { ApiService } from "@/app/services/AppService";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";
import { SearchServiceMobileCard } from "./SearchServiceMobileCard";
import MaxServiceAlert from "../genericComponents/card/MaxServiceAlert";
import ViewDetailsRsOneContainer from "../genericComponents/view-details/ViewDetailsRsOneContainer";
import AllCartsSlideSearch from "../genericComponents/all-carts/AllCartsSlideSearch";
import { SEARCH } from "./searchUi";

interface SearchServicesMobileProps {
  serviceName: any;
}

const ResultSkeleton = () => (
  <Box
    sx={{
      mb: 1.5,
      borderRadius: SEARCH.radiusMd,
      border: `1px solid ${SEARCH.border}`,
      p: 2,
      bgcolor: alpha(SEARCH.primary, 0.03),
    }}
  >
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Skeleton variant="rounded" width={88} height={88} sx={{ borderRadius: 2 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton width="70%" height={22} sx={{ mb: 1 }} />
        <Skeleton width="45%" height={16} sx={{ mb: 1.5 }} />
        <Skeleton width="40%" height={20} />
      </Box>
    </Box>
    <Skeleton sx={{ mt: 1.5 }} height={14} />
    <Skeleton width="88%" height={14} sx={{ mt: 0.5 }} />
  </Box>
);

const SearchServicesMobile: React.FC<SearchServicesMobileProps> = ({
  serviceName,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchServiceList, setSearchServiceList] = React.useState<
    ViewDetailsServices[] | undefined
  >(undefined);
  const [loading, setLoading] = React.useState(false);
  const [viewDetails, setViewDetails] = React.useState<
    ViewDetailsServices | undefined
  >(undefined);
  const [reloadViewDetailsItem, setReloadViewDetailsItem] =
    React.useState(false);
  const [updateCard, setUpdateCard] = React.useState(false);
  const [openMaxService, setOpenMaxService] = React.useState(false);
  const [openRsOneDetails, setOpenRsOneDetails] = React.useState(false);
  const [viewDetailsRsOneSid, setViewDetailsRsOneSid] = React.useState<
    number | undefined
  >(undefined);
  const apiService = React.useMemo(() => new ApiService(), []);
  const xHubId = getHubId();

  const handleOpenViewCart = (item: any) => {
    setOpen(true);
    setViewDetails(item);
  };

  const handleOpenViewDetailsRsOne = (basicProgram: BasicProgram) => {
    setOpenRsOneDetails(true);
    setViewDetailsRsOneSid(basicProgram?.id);
  };

  const getSearchServicesWithName = React.useCallback(async () => {
    if (!serviceName) {
      setSearchServiceList(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    setSearchServiceList(undefined);
    try {
      const responseObject: ResponseObject | undefined =
        await apiService.get<ResponseObject>(
          apiService.searchServicesWithName + serviceName,
          undefined,
          xHubId
        );
      const response = responseObject?.object;
      setSearchServiceList(Array.isArray(response) ? response : []);
    } catch {
      setSearchServiceList([]);
    } finally {
      setLoading(false);
    }
  }, [apiService, serviceName, xHubId]);

  React.useEffect(() => {
    getSearchServicesWithName();
  }, [serviceName, getSearchServicesWithName]);

  const count = searchServiceList?.length ?? 0;
  const showEmpty =
    !loading &&
    serviceName &&
    Array.isArray(searchServiceList) &&
    searchServiceList.length === 0;
  const showResults = !loading && count > 0;

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          maxWidth: "min(100%, 720px)",
          mx: "auto",
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          overflow: "auto",
          boxSizing: "border-box",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 999,
            background: alpha(SEARCH.primary, 0.25),
          },
        }}
      >
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: SEARCH.radiusMd,
            border: `1px solid ${alpha(SEARCH.primary, 0.12)}`,
            background: `linear-gradient(120deg, ${alpha(SEARCH.primary, 0.08)} 0%, ${alpha(
              "#f2c46c",
              0.1
            )} 100%)`,
          }}
        >
          <Typography
            sx={{
              fontFamily: SEARCH.fontDisplay,
              fontWeight: 700,
              fontSize: "1.2rem",
              letterSpacing: "-0.02em",
              color: SEARCH.text,
            }}
          >
            Results
          </Typography>
          {serviceName ? (
            <Typography sx={{ mt: 0.5, fontSize: "0.88rem", color: SEARCH.muted }}>
              “{serviceName}”
              {!loading && count > 0 ? (
                <Box component="span" sx={{ color: SEARCH.mutedLight }}>
                  {" "}
                  · {count} {count === 1 ? "match" : "matches"}
                </Box>
              ) : null}
            </Typography>
          ) : null}
        </Box>

        {loading ? (
          <>
            <ResultSkeleton />
            <ResultSkeleton />
          </>
        ) : showEmpty ? (
          <Box
            sx={{
              py: 5,
              px: 2,
              textAlign: "center",
              borderRadius: SEARCH.radiusMd,
              border: `1px dashed ${SEARCH.border}`,
              bgcolor: alpha(SEARCH.primary, 0.03),
            }}
          >
            <SearchOffIcon
              sx={{ fontSize: 48, color: alpha(SEARCH.primary, 0.35), mb: 1.25 }}
              aria-hidden
            />
            <Typography
              sx={{
                fontFamily: SEARCH.fontDisplay,
                fontWeight: 700,
                fontSize: "1.05rem",
                color: SEARCH.text,
                mb: 0.5,
              }}
            >
              No services found
            </Typography>
            <Typography sx={{ fontSize: "0.88rem", color: SEARCH.muted, lineHeight: 1.5 }}>
              Try another keyword from suggestions.
            </Typography>
          </Box>
        ) : showResults ? (
          searchServiceList?.map((item: any) => (
            <Box
              key={item?.id ?? item?.name}
              onClick={() => handleOpenViewCart(item)}
              sx={{
                cursor: "pointer",
                mb: 1.5,
                borderRadius: SEARCH.radiusMd,
                border: `1px solid ${SEARCH.border}`,
                overflow: "hidden",
                bgcolor: SEARCH.surfaceElevated,
                boxShadow: SEARCH.shadowCard,
                transition: "box-shadow 0.2s ease",
                "&:hover": { boxShadow: SEARCH.shadowCardHover },
                "&:last-of-type": { mb: 0 },
              }}
            >
              <SearchServiceMobileCard
                item={item}
                setReloadViewDetailsItem={(value: any) =>
                  setReloadViewDetailsItem(value)
                }
                updateCard={updateCard}
                setUpdateCard={() => setUpdateCard(false)}
                setOpenMaxService={setOpenMaxService}
                handleOpenViewDetailsRsOne={(item: any) =>
                  handleOpenViewDetailsRsOne(item)
                }
              />
            </Box>
          ))
        ) : null}

        <AllCartsSlideSearch />
      </Box>
      <ViewDetails
        open={open}
        setClose={() => setOpen(false)}
        imageUrl={
          viewDetails?.banner?.secureHttpPath ??
          viewDetails?.coverImage?.secureHttpPath
        }
        heading={viewDetails?.name}
        servicedDetails={viewDetails?.aboutList}
        descriptions={viewDetails?.descriptions}
        amount={viewDetails?.amount}
        min={viewDetails?.min}
        id={viewDetails?.id}
        cid={viewDetails?.cid}
        subHeading={viewDetails?.subHeading}
        mrp={viewDetails?.mrp}
        specialPrice={viewDetails?.specialPrice}
        specialMessage={viewDetails?.specialMessage}
        coin={viewDetails?.cashbackSalontymCoins}
        aboutList={viewDetails?.aboutList}
        serviceCharge={viewDetails?.serviceCharge}
        productCost={viewDetails?.productCost}
        reloadCartItemsFlag={() => setUpdateCard(true)}
        reloadViewDetailsItem={reloadViewDetailsItem}
        setReloadViewDetailsItem={() => {
          setReloadViewDetailsItem(false);
        }}
      />
      <MaxServiceAlert
        openInfo={openMaxService}
        handleClose={() => setOpenMaxService(false)}
      />
      <ViewDetailsRsOneContainer
        open={openRsOneDetails}
        handleCloseStoreDialog={() => setOpenRsOneDetails(false)}
        viewDetailsRsOneSid={viewDetailsRsOneSid}
      />
    </React.Fragment>
  );
};

export default SearchServicesMobile;
